import { Component, OnDestroy } from "@angular/core";
import { Subject, BehaviorSubject, forkJoin } from "rxjs";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Sort } from "@angular/material/sort";
import { GroupsService } from "../services/groups.service";
import { flatMap, tap, takeUntil, debounceTime, filter } from "rxjs/operators";
import { ActivatedRoute } from "@angular/router";
import { SelectMultipleGroupsDialogComponent } from "./selectMultipleGroupsDialog.component";
import { StreamService } from "../services/stream.service";

@Component({
    styles: [`
        .groupsPanel {
            overflow-y: auto;
            height: calc(100vh - 130px);
        }
        .cdk-column-description.mat-column-description {
            width: calc(100vw - 200px);
        }
        .cdk-column-members.mat-column-members {
            width: 85px;
            text-align: center;
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Groups</h3>
                <span fxFlex></span>
                <mat-form-field style="font-size: 16px; font-weight: 400;">
                    <input matInput placeholder="Search Term" [formControl]="searchControl">
                </mat-form-field>
                <button mat-button color="primary" (click)="add()">Add Groups</button>
            </mat-toolbar>
            <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
            <div class="groupsPanel"
                infiniteScroll 
                [infiniteScrollThrottle]="300"
                [infiniteScrollDistance]="1"
                [scrollWindow]="false"
                (scrolled)="nextPage()">
                <table mat-table
                    matSort 
                    (matSortChange)="sort($event)"
                    [matSortDisableClear]="true "
                    [matSortActive]="params?.value?.sort" 
                    [matSortDirection]="params?.value?.sortDirection" 
                    *ngIf="!noTouching" 
                    [dataSource]="groups" 
                    style="width: 100%;">
                    <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="nameLower" width="20%">Name</th>
                        <td mat-cell *matCellDef="let row"><a [routerLink]="['/settings/groups', row.id]">{{row.name}}</a></td>
                    </ng-container>
                    <ng-container matColumnDef="description">
                        <th mat-header-cell *matHeaderCellDef>Description</th>
                        <td mat-cell *matCellDef="let row">{{row.description}}</td>
                    </ng-container>
                    <ng-container matColumnDef="members">
                        <th mat-header-cell
                            *matHeaderCellDef 
                            mat-sort-header="membersCount" 
                            style="padding-left: 15px;">Members</th>
                        <td mat-cell *matCellDef="let row">{{row.membersCount}}</td>
                    </ng-container>
                    <ng-container matColumnDef="created">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="created">Created</th>
                        <td mat-cell *matCellDef="let row">{{row.created | date:'shortDate'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef  width="8%"></th>
                        <td mat-cell *matCellDef="let row">
                            <button mat-icon-button (click)="delete(row)"><mat-icon>clear</mat-icon></button>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="['name', 'description', 'members', 'created', 'actions']"></tr>
                    <tr mat-row *matRowDef="let row; columns: ['name', 'description', 'members', 'created', 'actions'];"></tr>
                </table>
                <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                    <spinner></spinner>
                </div>
            </div>
        </div>
    `
})
export class EditTopicGroupsComponent implements OnDestroy {
    searchControl = new FormControl();
    noTouching: boolean;
    noTouchingPaging: boolean;
    private cleanupTime = new Subject<void>();
    params = new BehaviorSubject<any>({ 
        sort: 'nameLower',
        sortDirection: 'asc',
        page: 0, 
        rows: 100
    });
    groups = new BehaviorSubject<any[]>([]);
    lastFetched: boolean;
    topicId: any;
    
    constructor(route: ActivatedRoute,
                private snackBar: MatSnackBar, 
                private dialog: MatDialog,
                private streamService: StreamService,
                private groupsService: GroupsService) {

        route.parent.params
        .pipe(
            takeUntil(this.cleanupTime),
            tap(params => this.topicId = params.topicId)
        ).subscribe((params) => this.params.next(Object.assign({}, this.params.value, {streamId: params.topicId , page: 0 })));

        this.params
        .pipe(
            filter(params => !!params.streamId),
            flatMap(params => streamService.fetchStreamGroups(params.streamId, params)),
            tap(() => this.noTouchingPaging = false),
            tap(groupRes => this.lastFetched = groupRes.last),
            takeUntil(this.cleanupTime)
        ).subscribe(sources => this.groups.next(this.groups.value.concat(sources.content)));
            
        this.searchControl.valueChanges
        .pipe(
            debounceTime(500),
            takeUntil(this.cleanupTime),
            tap(() => this.groups.next([]))
        ).subscribe(searchTerm => this.params.next(Object.assign({}, this.params.value, {searchTerm, page: 0})));
    }

    add() {
        this.dialog.open(SelectMultipleGroupsDialogComponent, { data: { existingGroupIds: this.groups.value.map(group => group.id) } }).afterClosed()
        .pipe(
            filter(groups => !!groups),
            flatMap(groups => forkJoin(groups.map(group => this.groupsService.addToStream(group.id, this.topicId)))),
            tap(() => this.refetch()),
        ).subscribe(results => this.snackBar.open(`${results.length} group${results.length > 1 ? 's' : ''} added.`, 'OK', { duration: 3000}));
    }

    private refetch() {
        this.noTouchingPaging = true;
        this.groups.next([]);
        this.params.next(Object.assign({}, this.params.value, { page: 0 }));
    }

    delete(group): void {
        this.groupsService.removeTopic(group.id, this.topicId)
        .subscribe(() => {
            this.snackBar.open(`${group.name} has been removed.`, 'OK', { duration: 3000});
            this.refetch();
        });
    }

    nextPage(): void {
        const params = this.params.value;
        params.page = params.page + 1;
        this.noTouchingPaging = true;
        this.params.next(params);
    }

    sort(sort: Sort): void {
        const params = this.params.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.noTouchingPaging = true;
        this.groups.next([]);
        this.params.next(params);
    }

    ngOnDestroy(): void {
        this.cleanupTime.complete();
        this.cleanupTime.next();
    }
}